import type { LibraryEditorValues } from "./library-editor-model"
import type { FilthSources } from "@/api/filth-sources-api"

/** Bounded paging: enough for any real deployment, never an unbounded loop. */
export const MAX_FILTH_SOURCE_PAGES = 20

/** A deployment change cannot carry another deployment's connector selection. */
export function withFilthSourceSelection(
  previous: LibraryEditorValues,
  patch: Partial<LibraryEditorValues>
): LibraryEditorValues {
  const changedBackend =
    (patch.filth_backend_id !== undefined &&
      patch.filth_backend_id !== previous.filth_backend_id) ||
    (patch.search_backend !== undefined && patch.search_backend !== previous.search_backend)
  return { ...previous, ...patch, ...(changedBackend ? { filth_source_uids: [] } : {}) }
}

export function toggleFilthSource(selected: string[], uid: string, checked: boolean): string[] {
  if (!checked) return selected.filter((value) => value !== uid)
  if (selected.includes(uid) || selected.length >= 64) return selected
  return [...selected, uid]
}

/**
 * Follow the `/sources` cursor so a source on a later page can be selected.
 * Stops at `maxPages` or on a repeated cursor; a non-null `next` in the result
 * means the list is incomplete and the picker says so.
 */
export async function collectFilthSourcePages(
  fetchPage: (cursor?: string) => Promise<FilthSources>,
  maxPages = MAX_FILTH_SOURCE_PAGES
): Promise<FilthSources> {
  const connectors: FilthSources["connectors"] = []
  const seenUids = new Set<string>()
  const seenCursors = new Set<string>()
  let cursor: string | undefined
  for (let page = 0; page < maxPages; page++) {
    const result = await fetchPage(cursor)
    for (const source of result.connectors) {
      if (seenUids.has(source.connector_uid)) continue
      seenUids.add(source.connector_uid)
      connectors.push(source)
    }
    const next = result.next || undefined
    if (!next) return { connectors, next: null }
    if (seenCursors.has(next)) return { connectors, next }
    seenCursors.add(next)
    cursor = next
  }
  return { connectors, next: cursor ?? null }
}
